import { createTheme } from '@mui/material/styles';

// Electure brand palette: #7E9F7E sage | #D3E4D3 mint | #79BD79 green
// #FFFFFF white | #084608 forest | #C2422B terracotta
// Fonts: Inter (UI — uniform across app)
export const theme = createTheme({
  palette: {
    primary: {
      main: '#084608',   // Forest green — primary actions
      light: '#7E9F7E',  // Sage green
      dark: '#042e04',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#C2422B',   // Terracotta — accent
      light: '#d96e58',
      dark: '#8f2f1d',
      contrastText: '#ffffff',
    },
    background: {
      default: '#EEF5EE', // Very light sage
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2e1a',   // Very dark green
      secondary: '#4a6b4a', // Muted green
      disabled: '#9db89d',
    },
    divider: '#C8DCC8',   // Light sage border
    success: {
      main: '#79BD79',
      light: '#D3E4D3',
      dark: '#084608',
    },
    warning: {
      main: '#F59E0B',
      light: '#FEF3C7',
      dark: '#B45309',
    },
    error: {
      main: '#C2422B',
      light: '#f5d0c8',
      dark: '#8f2f1d',
    },
    info: {
      main: '#7E9F7E',
      light: '#D3E4D3',
      dark: '#4a6b4a',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
  typography: {
    fontSize: 15,
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em', color: '#084608', fontSize: '2.25rem' },
    h2: { fontWeight: 700, color: '#084608', fontSize: '1.75rem' },
    h3: { fontWeight: 700, color: '#084608', fontSize: '1.375rem' },
    h4: { fontWeight: 700, color: '#1a2e1a', fontSize: '1.25rem' },
    h5: { fontWeight: 600, color: '#1a2e1a', fontSize: '1.125rem' },
    h6: { fontWeight: 600, color: '#1a2e1a', fontSize: '1rem' },
    subtitle1: { fontWeight: 500, color: '#4a6b4a', fontSize: '1rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 500, color: '#4a6b4a', fontSize: '0.875rem', lineHeight: 1.4 },
    body1: { color: '#2d4a2d', lineHeight: 1.6, fontSize: '1rem' },
    body2: { color: '#4a6b4a', lineHeight: 1.5, fontSize: '0.875rem' },
    caption: { color: '#7E9F7E', fontSize: '0.75rem', letterSpacing: '0.02em' },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '1rem', letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(15, 23, 42, 0.04)',
    '0px 1px 4px rgba(15, 23, 42, 0.06), 0px 1px 2px rgba(15, 23, 42, 0.04)',
    '0px 4px 8px -1px rgba(15, 23, 42, 0.08), 0px 2px 4px -1px rgba(15, 23, 42, 0.04)',
    '0px 8px 16px -3px rgba(15, 23, 42, 0.1), 0px 4px 6px -2px rgba(15, 23, 42, 0.04)',
    '0px 16px 24px -5px rgba(15, 23, 42, 0.12), 0px 8px 10px -5px rgba(15, 23, 42, 0.04)',
    ...Array(19).fill('none')
  ] as any,
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'var(--color-bg)',
          color: 'var(--color-text)',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 1px 4px rgba(15, 23, 42, 0.06), 0px 1px 2px rgba(15, 23, 42, 0.04)',
          border: '1px solid #E8EDF2',
          backgroundImage: 'none',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        },
      },
      defaultProps: { elevation: 0 },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 10 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(12px)',
          borderBottom: '2px solid #D3E4D3',
          boxShadow: 'none',
          color: '#084608',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#084608',
          borderRight: 'none',
          color: '#D3E4D3',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #F1F5F9',
          padding: '10px 14px',
          fontSize: '0.875rem',
        },
        head: {
          backgroundColor: '#F8FAFC',
          color: '#475569',
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          padding: '10px 14px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          height: 22,
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        },
        label: { padding: '0 8px' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#64748B',
          borderRadius: 8,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: '#F1F5F9',
            color: '#0F172A',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '7px 16px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          transition: 'all 0.15s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0px 2px 8px rgba(15, 23, 42, 0.15)' },
        },
        outlined: {
          borderColor: '#E2E8F0',
          color: '#475569',
          '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#CBD5E1' },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: { '&.MuiGrid-item': { paddingTop: 8 } },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6 },
      },
    },
  },
});

export default theme;
